import { Capacitor } from '@capacitor/core';
import { Trans, t } from '@lingui/macro';
import { useEffect, useReducer, useState, useRef } from 'react';
import { useForm } from "react-hook-form";
import { useHistory } from '../../hooks/useRouterCompat';
import { Fire } from '../../services/FireClient';
import { My } from '../../services/My';
import { UtilApp } from '../../services/Utils/UtilApp';
import ProfileImg from '../components/ProfileImg';
import SelectProfileImageModal from '../components/SelectProfileImageModal';
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { ArrowLeft, XCircle, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { cn } from "../../lib/utils";

const LoginPage: React.FC = () => {
  const history = useHistory();
  const [inProgress, setInProgress] = useState(false);
  const [loginState, setLoginState] = useState<'start' | 'register' | 'profile' | 'login' | 'token_login' | 'choose-provider' | 'email-verification' | 'set-password-from-link'>('start');
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [repeatedPassword, setRepeatedPassword] = useState("");
  const [loginName, setUserName] = useState("");
  const [loginImage, setLoginImage] = useState("/assets/profiles/anonymous.svg");
  const { register, handleSubmit, formState: { errors } } = useForm();
  const [loginFailedMsg, setLoginFailedMsg] = useState("");
  const [loadingTarget, setLoadingTarget] = useState<string | null>(null);
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  const [showAppleSignin] = useState(Capacitor.getPlatform() === 'ios');
  const passwordInputRef = useRef<HTMLInputElement>(null);

  // 계정 정보 (provider 정보 저장)
  const [accountInfo, setAccountInfo] = useState<{ exists: boolean; hasPassword: boolean; hasGoogle: boolean; hasApple: boolean } | null>(null);

  // Alert state
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState<{
    title: string;
    message: string;
    buttons: { text: string; role?: string; handler?: () => void }[];
  }>({ title: '', message: '', buttons: [] });

  useEffect(() => {
    redirectIfLoggedIn();

    // 이메일 인증 링크로 접근했는지 확인 (에뮬레이터 환경에서 안전하게 처리)
    try {
      if (Fire.auth.isEmailSignInLink()) {
        const storedEmail = Fire.auth.getStoredEmailForSignUp();
        if (storedEmail) {
          setLoginEmail(storedEmail);
          setLoginState('set-password-from-link');
        } else {
          // 다른 기기에서 링크를 열었을 경우 이메일 입력 요청
          showAlert(t`Email verification`, t`Please enter your email address to complete registration.`);
        }
      }
    } catch (e) {
      console.warn('Email sign-in link check failed:', e);
    }
  }, [])

  useEffect(() => {
    // bfcache 복원 대비: 리다이렉트 로그인을 취소하고 뒤로가기로 돌아오면 브라우저가
    // "로딩 중" 상태의 페이지를 그대로 되살리므로 여기서 로딩 상태를 해제한다 (QA-01)
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        setInProgress(false);
        setLoadingTarget(null);
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [])

  useEffect(() => {
    // Fade in animation
    const root = document.getElementById('root') || document.body;
    requestAnimationFrame(() => {
      root.style.transition = 'opacity 0.3s ease-in-out';
      root.style.opacity = '1';
    });

    // Auto focus password
    if (loginState === 'login') {
      setTimeout(() => {
        passwordInputRef.current?.focus();
      }, 100);
    }
  }, [loginState]);

  function getRedirectUrl() {
    return UtilApp.getParam('redirect') || '/main';
  }

  function redirectIfLoggedIn() {
    if (My.isLoggedIn()) {
      history.replace(getRedirectUrl());
    }
  }

  function showAlert(title: string, message: string, buttons: { text: string; role?: string; handler?: () => void }[] = [{ text: t`OK` }]) {
    setAlertConfig({ title, message, buttons });
    setAlertOpen(true);
  }

  async function loginWithGoogle() {
    setLoadingTarget('google');
    setInProgress(true);
    try {
      let error = await Fire.auth.loginUser('google');
      console.log("Google login error:", error);
      setLoginFailedMsg(error);
    } finally {
      setInProgress(false);
      setLoadingTarget(null);
    }
  }

  async function loginWithApple() {
    setLoadingTarget('apple');
    setInProgress(true);
    try {
      let error = await Fire.auth.loginUser('apple');
      setLoginFailedMsg(error);
    } finally {
      setInProgress(false);
      setLoadingTarget(null);
    }
  }

  function getTokenLoginProgress() {
    return (
      <div className="text-center py-4">
        <h4 className="text-lg font-medium"><Trans>Starting NEXTPOD</Trans></h4>
      </div>
    );
  }

  function getStartPage() {
    const emailRegistration = register('email', { required: true, maxLength: 50, pattern: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/ });

    return (
      <div className="space-y-6">
        <div className="text-center">
          <img src='assets/img/logo.png' alt="NEXTPOD Logo" className="h-12 mx-auto mb-4" />
          <p onClick={() => history.push('/terms')} className='text-sm text-muted-foreground cursor-pointer hover:underline'>
            <Trans>By signing in, you accept NEXTPOD <a>Terms of Service</a>.</Trans>
          </p>
        </div>

        <div className="space-y-3">
          <Button
            variant="outline"
            className="w-full h-12 bg-background hover:bg-muted/50 border border-solid border-border text-foreground font-normal"
            onClick={loginWithGoogle}
            disabled={inProgress}
          >
            {inProgress && loadingTarget === 'google' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <img src='/assets/icon/google-logo.png' className="w-5 h-5 mr-3" alt="Google" />}
            <Trans>Sign in with Google</Trans>
          </Button>

          {showAppleSignin && (
            <Button
              variant="outline"
              className="w-full h-12 bg-background hover:bg-muted/50 border border-border text-foreground font-normal shadow-sm"
              onClick={loginWithApple}
              disabled={inProgress}
            >
              {inProgress && loadingTarget === 'apple' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <img src='/assets/icon/apple-logo.png' className="w-5 h-5 mr-3" alt="Apple" />}
              <Trans>Sign in with Apple</Trans>
            </Button>
          )}
        </div>

        {LoginFailedMsg()}

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">
              <Trans>or enter with your email</Trans>
            </span>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email"><Trans>Email</Trans></Label>
            <div className="relative">
              <Input
                id="email"
                type="email"
                placeholder={t`Email`}
                value={loginEmail}
                readOnly={loginState !== 'start'}
                disabled={inProgress}
                className={cn(errors.email ? "border-destructive" : "")}
                {...emailRegistration}
                onChange={(e) => {
                  emailRegistration.onChange(e);
                  setLoginEmailValue(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSubmit(nextHandler)();
                  }
                }}
              />
            </div>
            {errors.email && errors.email.type === "required" && <p className="text-sm text-destructive"><Trans>Email is required</Trans></p>}
            {errors.email && errors.email.type === "maxLength" && <p className="text-sm text-destructive"><Trans>Max length 50 characters</Trans></p>}
            {errors.email && errors.email.type === "pattern" && <p className="text-sm text-destructive"><Trans>Email must be in xxx@yyy.zzz format.</Trans></p>}
          </div>

          <Button
            className="w-full h-12 text-base font-medium"
            disabled={inProgress}
            onClick={handleSubmit(nextHandler)}
          >
            {inProgress && loadingTarget === 'email-next' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            <Trans>Next</Trans>
          </Button>
        </div>
      </div>
    );
  }

  function LoginFailedMsg() {
    return loginFailedMsg && (
      <div className="flex items-start justify-between p-3 text-sm text-destructive bg-destructive/10 rounded-md">
        <span className="break-all whitespace-pre-wrap flex-1 mr-2">{loginFailedMsg}</span>
        <button onClick={() => { setInProgress(false); setLoginFailedMsg("") }} className="text-destructive hover:text-destructive/80 shrink-0 mt-0.5">
          <XCircle className="w-5 h-5" />
        </button>
      </div>
    );
  }

  function getLoginPage() {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 mb-6">
          <Button variant="ghost" size="icon" onClick={backToStart} className="-ml-2">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h3 className="text-lg font-semibold"><Trans>Login</Trans></h3>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">{t`Email`}</Label>
            <Input value={loginEmail} readOnly disabled className="bg-muted" />
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">{t`Enter your password`}</Label>
            <Input
              ref={passwordInputRef}
              type="password"
              placeholder={t`Password`}
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              disabled={inProgress}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSubmit(loginUserWithPassword)();
                }
              }}
            />
          </div>

          {LoginFailedMsg()}

          <Button
            className="w-full h-12"
            disabled={inProgress || !loginPassword}
            onClick={handleSubmit(loginUserWithPassword)}
          >
            {inProgress && loadingTarget === 'login' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            <Trans>Login</Trans>
          </Button>

          <div className="text-center">
            <button
              onClick={requestPasswordResetLink}
              className="text-sm text-muted-foreground hover:text-primary hover:underline"
            >
              <Trans>Forgotten your password?</Trans>
            </button>
          </div>
        </div>
      </div>
    );
  }

  function getChooseProviderPage() {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 mb-6">
          <Button variant="ghost" size="icon" onClick={backToStart} className="-ml-2">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h3 className="text-lg font-semibold"><Trans>Choose login method</Trans></h3>
        </div>

        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">{t`Email`}</Label>
          <Input value={loginEmail} readOnly disabled className="bg-muted" />
        </div>

        <div className="text-sm text-muted-foreground text-center py-2">
          <Trans>This account has the following login methods:</Trans>
        </div>

        <div className="space-y-3">
          {accountInfo?.hasGoogle && (
            <Button
              variant="outline"
              className="w-full h-12 bg-background hover:bg-muted/50 border border-solid border-border text-foreground font-normal"
              onClick={loginWithGoogle}
              disabled={inProgress}
            >
              {inProgress && loadingTarget === 'google' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <img src='/assets/icon/google-logo.png' className="w-5 h-5 mr-3" alt="Google" />}
              <Trans>Sign in with Google</Trans>
            </Button>
          )}

          {accountInfo?.hasApple && showAppleSignin && (
            <Button
              variant="outline"
              className="w-full h-12 bg-background hover:bg-muted/50 border border-border text-foreground font-normal shadow-sm"
              onClick={loginWithApple}
              disabled={inProgress}
            >
              {inProgress && loadingTarget === 'apple' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <img src='/assets/icon/apple-logo.png' className="w-5 h-5 mr-3" alt="Apple" />}
              <Trans>Sign in with Apple</Trans>
            </Button>
          )}

          {accountInfo?.hasPassword ? (
            <Button
              className="w-full h-12"
              onClick={() => setLoginState('login')}
              disabled={inProgress}
            >
              <Trans>Sign in with password</Trans>
            </Button>
          ) : (
            <Button
              variant="outline"
              className="w-full h-12"
              onClick={async () => {
                setInProgress(true);
                await Fire.auth.sendResetPasswordLink(loginEmail);
                setInProgress(false);
                showAlert(t`Email sent`, t`Password setup email has been sent. Please check your inbox.`);
              }}
              disabled={inProgress}
            >
              <Trans>Set up password login</Trans>
            </Button>
          )}
        </div>

        {LoginFailedMsg()}
      </div>
    );
  }

  function getEmailVerificationPage() {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 mb-6">
          <Button variant="ghost" size="icon" onClick={backToStart} className="-ml-2">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h3 className="text-lg font-semibold"><Trans>Verify your email</Trans></h3>
        </div>

        <div className="text-center space-y-4 py-8">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <p className="font-medium"><Trans>Verification email sent!</Trans></p>
            <p className="text-sm text-muted-foreground mt-2">
              <Trans>We've sent a verification link to:</Trans>
            </p>
            <p className="font-medium mt-1">{loginEmail}</p>
          </div>
          <p className="text-sm text-muted-foreground">
            <Trans>Please check your inbox and click the link to complete registration.</Trans>
          </p>
        </div>

        <Button
          variant="outline"
          className="w-full"
          onClick={async () => {
            setInProgress(true);
            await Fire.auth.sendEmailVerificationLink(loginEmail);
            setInProgress(false);
            showAlert(t`Email sent`, t`Verification email has been resent.`);
          }}
          disabled={inProgress}
        >
          {inProgress && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          <Trans>Resend verification email</Trans>
        </Button>
      </div>
    );
  }

  function getSetPasswordFromLinkPage() {
    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h3 className="text-lg font-semibold"><Trans>Complete your registration</Trans></h3>
          <p className="text-sm text-muted-foreground mt-2">{loginEmail}</p>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground"><Trans>Create your password</Trans></Label>
            <Input
              type="password"
              placeholder={t`New password`}
              value={loginPassword}
              onInput={(e) => setLoginPassword((e.target as HTMLInputElement).value)}
              disabled={inProgress}
              {...register('password', { required: true, minLength: 6, validate: value => /[^a-zA-Z0-9]/.test(value) })}
            />
            {errors.password && errors.password.type === "required" && <p className="text-sm text-destructive"><Trans>This is required</Trans></p>}
            {errors.password && errors.password.type === "minLength" && <p className="text-sm text-destructive"><Trans>Password must be at least 6 characters.</Trans></p>}
            {errors.password && errors.password.type === "validate" && <p className="text-sm text-destructive"><Trans>Password must contain at least one special character.</Trans></p>}
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground"><Trans>Confirm your password</Trans></Label>
            <Input
              type="password"
              placeholder={t`Repeat your password`}
              value={repeatedPassword}
              onChange={(e) => setRepeatedPassword(e.target.value)}
              disabled={inProgress}
            />
          </div>

          <div className="space-y-2">
            <Label><Trans>Name</Trans></Label>
            <Input
              placeholder={t`Enter your name`}
              value={loginName}
              maxLength={30}
              onInput={(e) => setUserName((e.target as HTMLInputElement).value)}
              disabled={inProgress}
              {...register('name', { required: true })}
            />
            {errors.name && errors.name.type === "required" && <p className="text-sm text-destructive"><Trans>This is required</Trans></p>}
          </div>

          {LoginFailedMsg()}

          <Button
            className="w-full h-12"
            disabled={inProgress || !loginPassword || !repeatedPassword || !loginName}
            onClick={handleSubmit(completeEmailSignUp)}
          >
            {inProgress && loadingTarget === 'signup' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            <Trans>Complete registration</Trans>
          </Button>
        </div>
      </div>
    );
  }

  async function completeEmailSignUp() {
    if (loginPassword !== repeatedPassword) {
      setLoginFailedMsg(t`Password do not match.`);
      return;
    }

    setLoadingTarget('signup');
    setInProgress(true);
    try {
      await Fire.auth.completeEmailSignUp(loginEmail, loginPassword, loginName, loginImage);
      history.replace(getRedirectUrl());
    } catch (error: any) {
      setLoginFailedMsg(error.message);
    }
    setInProgress(false);
  }

  function getRegistrationPage() {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 mb-6">
          <Button variant="ghost" size="icon" onClick={backToStart} className="-ml-2">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h3 className="text-lg font-semibold"><Trans>Create a new account</Trans></h3>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground"><Trans>Email</Trans></Label>
            <Input value={loginEmail} readOnly disabled className="bg-muted" />
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground"><Trans>Create your password</Trans></Label>
            <Input
              type="password"
              placeholder={t`New password`}
              value={loginPassword}
              onInput={(e) => setLoginPassword((e.target as HTMLInputElement).value)}
              disabled={inProgress}
              {...register('password', { required: true, minLength: 6, validate: value => /[^a-zA-Z0-9]/.test(value) })}
            />
            {errors.password && errors.password.type === "required" && <p className="text-sm text-destructive"><Trans>This is required</Trans></p>}
            {errors.password && errors.password.type === "minLength" && <p className="text-sm text-destructive"><Trans>Password must be at least 6 characters.</Trans></p>}
            {errors.password && errors.password.type === "validate" && <p className="text-sm text-destructive"><Trans>Password must contain at least one special character.</Trans></p>}
          </div>

          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground"><Trans>Confirm your password</Trans></Label>
            <Input
              type="password"
              placeholder={t`Repeat your password`}
              value={repeatedPassword}
              onChange={(e) => setRepeatedPassword(e.target.value)}
              disabled={inProgress}
            />
          </div>

          {LoginFailedMsg()}

          <Button
            className="w-full h-12"
            disabled={inProgress || !repeatedPassword}
            onClick={handleSubmit(profileHandler)}
          >
            <Trans>Next</Trans>
          </Button>
        </div>
      </div>
    );
  }

  function getProfilePage() {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 mb-6">
          <Button variant="ghost" size="icon" onClick={() => setLoginState('register')} className="-ml-2">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h3 className="text-lg font-semibold"><Trans>Create a new account</Trans></h3>
        </div>

        <div className="space-y-6">
          <div className="flex flex-col items-center gap-4">
            <Label><Trans>Profile Image</Trans></Label>
            <div className="relative w-24 h-24">
              <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-border">
                <ProfileImg url={loginImage} className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-2 -right-2">
                <SelectProfileImageModal loginImage={loginImage} onSelected={profileImgchanged} />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label><Trans>Name</Trans></Label>
            <Input
              placeholder={t`Enter your name`}
              value={loginName}
              maxLength={30}
              onInput={(e) => setUserName((e.target as HTMLInputElement).value)}
              disabled={inProgress}
              {...register('name', { required: true })}
            />
            {errors.name && errors.name.type === "required" && <p className="text-sm text-destructive"><Trans>This is required</Trans></p>}
          </div>

          {LoginFailedMsg()}

          <Button
            className="w-full h-12"
            disabled={inProgress || !loginName}
            onClick={handleSubmit(registerNewUser)}
          >
            {inProgress && loadingTarget === 'signup' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            <Trans>Sign up</Trans>
          </Button>
        </div>
      </div>
    );
  }

  function handleTouchEvent(e: any) {
    e.preventDefault();
    setUserName("");
  }

  function backToStart() {
    setLoginState('start');
    setLoginFailedMsg("");
    setInProgress(false);
    setLoadingTarget(null);
  }

  async function profileImgchanged(url: string) {
    setLoginImage(url)
  }

  function setLoginEmailValue(value: string) {
    setLoginEmail(value.trim().toLowerCase());
    forceUpdate();
  }

  async function nextHandler() {
    let lower = loginEmail.trim().toLowerCase();
    setLoginEmail(lower)
    setLoginPassword("");
    setRepeatedPassword("");
    setLoadingTarget('email-next');
    setInProgress(true);

    try {
      const info = await Fire.auth.checkAccountInfo(lower);
      setAccountInfo(info);

      if (info.exists) {
        // 기존 계정
        if (info.hasGoogle || info.hasApple) {
          // 소셜 로그인이 있으면 선택 화면으로
          setLoginState('choose-provider');
        } else {
          // 소셜 로그인 없으면 바로 비밀번호 입력
          setLoginState('login');
        }
      } else {
        // 신규 가입 - 이메일 인증 발송 (일시 중단)
        // await Fire.auth.sendEmailVerificationLink(lower);
        // setLoginState('email-verification');
        setLoginState('register'); // 기존 회원가입 플로우로 복귀
      }
    } catch (error: any) {
      showAlert(t`Error`, t`Something went wrong, please try again.` + ` Error: ${error.message}. ${error.code}.`);
    }
    setInProgress(false);
  }

  function profileHandler() {
    if (loginPassword != repeatedPassword) {
      setLoginFailedMsg(t`Password do not match.`);
      return;
    }
    setLoginState('profile');
  }

  async function loginUserWithPassword() {
    setLoadingTarget('login');
    setInProgress(true);
    setLoginFailedMsg("");
    let result = await Fire.auth.loginUserWithPassword(loginEmail, loginPassword);
    if (result == 'wrong-pass') {
      setLoginFailedMsg(t`Wrong password.`);
    } else if (result == 'too-many-tries') {
      setLoginFailedMsg(t`Too many attempts. Try a bit later.`);
    } else if (result == 'no-password') {
      setLoginFailedMsg(t`This account doesn't have a password. Please use Google or Apple login, or reset your password.`);
    } else if (result == 'error') {
      setLoginFailedMsg(t`Something went wrong, please try again.`);
    }

    if (result !== 'success') {
      setInProgress(false);
    } else {
      // Successful login
      history.replace(getRedirectUrl());
    }
  }

  async function registerNewUser() {
    setLoadingTarget('signup');
    setInProgress(true);
    try {
      await Fire.auth.registerNewUser(loginEmail, loginName, loginPassword, loginImage);
      history.replace(getRedirectUrl());
    } catch (error) {
      setLoginFailedMsg((error as Error).message)
    }
    setInProgress(false);
  }

  function requestPasswordResetLink() {
    showAlert(
      t`Forgotten your password?`,
      loginEmail,
      [
        { text: t`Cancel`, role: 'cancel' },
        {
          text: t`Send me a reset password link`,
          handler: async () => {
            await Fire.auth.sendResetPasswordLink(loginEmail);
            // We need to show another alert after this one closes, or just toast.
            // For simplicity, let's use a timeout or just log it for now as the alert system is simple.
            // Better: update the alert to show success.
            setTimeout(() => {
              showAlert(t`Success`, t`Sent! Your email might end up in your spam folder.`);
            }, 500);
          }
        }
      ]
    );
  }

  return (
    <div className="min-h-screen w-full bg-page-brand flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-background shadow-xl border-none">
        <CardContent className="p-6 sm:p-8">
          {loginState == 'start' && getStartPage()}
          {loginState == 'login' && getLoginPage()}
          {loginState == 'register' && getRegistrationPage()}
          {loginState == 'profile' && getProfilePage()}
          {loginState == 'token_login' && getTokenLoginProgress()}
          {loginState == 'choose-provider' && getChooseProviderPage()}
          {loginState == 'email-verification' && getEmailVerificationPage()}
          {loginState == 'set-password-from-link' && getSetPasswordFromLinkPage()}
        </CardContent>
      </Card>

      <AlertDialog open={alertOpen} onOpenChange={setAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{alertConfig.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {alertConfig.message}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row justify-end gap-2">
            {alertConfig.buttons.map((btn, index) => (
              btn.role === 'cancel' ? (
                <AlertDialogCancel
                  key={index}
                  className="mt-0 border-border border border-solid"
                  onClick={() => {
                    setAlertOpen(false);
                    if (btn.handler) btn.handler();
                  }}
                >
                  {btn.text}
                </AlertDialogCancel>
              ) : (
                <AlertDialogAction
                  key={index}
                  className="mt-0"
                  onClick={() => {
                    setAlertOpen(false);
                    if (btn.handler) btn.handler();
                  }}
                >
                  {btn.text}
                </AlertDialogAction>
              )
            ))}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LoginPage;
